import type { SubscribeFormTheme } from './types'

/**
 * Per-variant class strings. Full class names only (never interpolated) so
 * Tailwind can see them — see the code-style rule on dynamic classes.
 * Audiogram colors are concrete hex/rgba because they feed an SVG gradient.
 */
export interface SubscribeThemeTokens {
  card: string
  eyebrow: string
  heading: string
  subheading: string
  benefit: string
  benefitIcon: string
  input: string
  inputLabel: string
  button: string
  fineprint: string
  errorBox: string
  successIconWrap: string
  successIcon: string
  successHeading: string
  successMessage: string
  successReset: string
  /** Color of the decorative filled waveform along the card's bottom edge
   *  (inherited via `currentColor`). Kept faint — it sits behind the content. */
  wave: string
  /** Two layered radial-gradient background strings — soft brand-color blooms
   *  for depth. No lines; they sit behind the content and never cross it. */
  glowA: string
  glowB: string
}

const MIDNIGHT: SubscribeThemeTokens = {
  card: 'bg-[linear-gradient(150deg,#1c2d52_0%,#142546_52%,#101f3d_100%)] ring-1 ring-white/10 shadow-[0_30px_80px_-32px_rgba(10,20,45,0.7)]',
  eyebrow: 'text-sky',
  heading: 'text-white',
  subheading: 'text-white/65',
  benefit: 'text-white/70',
  benefitIcon: 'text-sky',
  input:
    'bg-white/[0.13] text-white ring-1 ring-white/25 placeholder:text-white/55 focus:bg-white/[0.17] focus:ring-white/40',
  inputLabel: 'text-white/75',
  button: 'bg-merigold text-navy hover:bg-merigold-offset active:scale-[0.99]',
  fineprint: 'text-white/40',
  errorBox: 'bg-white/10 text-amber-200',
  successIconWrap: 'bg-emerald-400/15 ring-1 ring-emerald-300/25',
  successIcon: 'text-emerald-300',
  successHeading: 'text-white',
  successMessage: 'text-white/65',
  successReset: 'text-sky hover:text-white',
  wave: 'text-sky/20',
  glowA: 'radial-gradient(55% 55% at 26% 16%, rgba(152,193,255,0.16), transparent 72%)',
  glowB: 'radial-gradient(46% 56% at 90% 98%, rgba(255,209,149,0.1), transparent 70%)',
}

const PAPER: SubscribeThemeTokens = {
  card: 'bg-cream ring-1 ring-navy/10 shadow-[0_30px_80px_-32px_rgba(20,37,70,0.28)]',
  eyebrow: 'text-lapis',
  heading: 'text-navy',
  subheading: 'text-navy/65',
  benefit: 'text-navy/70',
  benefitIcon: 'text-lapis',
  input: 'bg-white text-navy ring-1 ring-navy/20 placeholder:text-navy/55 focus:ring-lapis/50',
  inputLabel: 'text-navy/70',
  button: 'bg-navy text-white hover:bg-navy-90 active:scale-[0.99]',
  fineprint: 'text-navy/45',
  errorBox: 'bg-error/5 text-error ring-1 ring-error/20',
  successIconWrap: 'bg-emerald-500/10 ring-1 ring-emerald-500/20',
  successIcon: 'text-emerald-600',
  successHeading: 'text-navy',
  successMessage: 'text-navy/65',
  successReset: 'text-lapis hover:text-navy',
  // Deeper than the midnight variant's wave on purpose: on a cream card the
  // same opacity all but disappears, leaving the card bottom-light against a
  // heavy heading. This weights the lower edge so the card reads balanced.
  wave: 'text-lapis/35',
  glowA: 'radial-gradient(55% 55% at 22% 12%, rgba(74,127,229,0.1), transparent 72%)',
  glowB: 'radial-gradient(48% 56% at 92% 98%, rgba(255,195,119,0.14), transparent 70%)',
}

export const SUBSCRIBE_THEME: Record<SubscribeFormTheme, SubscribeThemeTokens> = {
  midnight: MIDNIGHT,
  paper: PAPER,
}
